import {
    collection, doc, documentId, getDocs, limit, orderBy, query,
    runTransaction, serverTimestamp, startAfter, where,
} from 'firebase/firestore';
import { auth, db } from '../firebase';

// Keep offline documentation purchases outside every ticketing collection.
const collectionName = 'documentationCustomers';
export const isDocumentationCustomer = record => record?.recordType === 'documentation_only';
export const newDocumentationCustomer = eventId => ({
    id: doc(collection(db, collectionName)).id, eventId,
});

export async function loadDocumentationCustomers(eventId) {
    const customers = [];
    let cursor;
    while (true) {
        const constraints = [where('eventId', '==', eventId), orderBy(documentId()), limit(200)];
        if (cursor) constraints.push(startAfter(cursor));
        const page = await getDocs(query(collection(db, collectionName), ...constraints));
        customers.push(...page.docs.map(snapshot => ({
            ...snapshot.data(), id: snapshot.id,
            // Source determines row behavior, even if a stored marker is malformed.
            recordType: 'documentation_only', withoutSeat: true,
        })));
        if (page.docs.length < 200) return customers;
        cursor = page.docs[page.docs.length - 1];
    }
}

export async function saveDocumentationCustomer({ id, eventId, userName, adminNote, expectedRevision }) {
    const actor = auth.currentUser?.uid;
    if (!actor) throw new Error('Please sign in again.');
    if (![id, eventId].every(value => typeof value === 'string' && value.trim() && !value.includes('/'))) {
        throw new Error('Select an event and reopen the documentation form.');
    }
    const name = typeof userName === 'string' ? userName.trim() : '';
    const note = typeof adminNote === 'string' ? adminNote.trim() : '';
    if (!name || name.length > 200) throw new Error('Enter a customer name within 200 characters.');
    if (note.length > 2000) throw new Error('Please keep the note within 2,000 characters.');
    const creating = expectedRevision === undefined;
    if (!creating && (!Number.isInteger(expectedRevision) || expectedRevision < 1)) {
        throw new Error('This record needs review. Refresh the list before editing.');
    }

    return runTransaction(db, async transaction => {
        const customerRef = doc(db, collectionName, id);
        const event = await transaction.get(doc(db, 'events', eventId));
        const snapshot = await transaction.get(customerRef);
        if (!event.exists()) throw new Error('This event no longer exists.');
        const previous = snapshot.exists() ? snapshot.data() : null;
        if (previous && (previous.eventId !== eventId || !isDocumentationCustomer(previous)
            || previous.withoutSeat !== true || previous.paymentStatus !== 'paid')) {
            throw new Error('This record has changed. Refresh the list before editing.');
        }
        if (creating && previous) {
            // Reuse the modal's ID after a lost acknowledgement instead of adding a duplicate.
            if (previous.createdBy === actor && previous.userName === name && previous.adminNote === note) {
                return { ...previous, id };
            }
            throw new Error('This record already exists. Refresh the list before editing.');
        }
        if (!creating && (!previous || previous.revision !== expectedRevision)) {
            throw new Error('Another staff member changed or removed this record. Copy your draft, cancel and refresh the list.');
        }
        const patch = {
            userName: name, adminNote: note, revision: creating ? 1 : expectedRevision + 1,
            updatedAt: serverTimestamp(), updatedBy: actor,
        };
        if (creating) {
            const data = {
                ...patch, eventId, recordType: 'documentation_only', withoutSeat: true,
                paymentStatus: 'paid', createdAt: serverTimestamp(), createdBy: actor,
            };
            transaction.set(customerRef, data);
            return { ...data, id, createdAt: new Date(), updatedAt: new Date() };
        }
        transaction.update(customerRef, patch);
        return { ...previous, ...patch, id, updatedAt: new Date() };
    });
}
